import React from 'react'
import {useSelector} from 'react-redux'
import {useCheckPriceWatchMismatch} from 'components/PriceWatch/hooks/useCheckPriceWatchMismatch'
import {getCurrencyCode} from 'modules/meta/selectors'
import {
  getNumberOfGuests,
  getNumberOfRooms,
  getShowTotalPrices,
  getSplitBookingPriceSummary
} from 'modules/sapiSearch/selectors'
import {getClickedOfferRate} from 'modules/search/selectors'
import {getIsMultiRoomBundlesSearch} from 'modules/search/selectors'

import {Box} from '@vio/atlas/helpers/Box'
import {hasAnchorPrice} from '@vio/core/src/offer/business/price'
import {SearchOffer} from '@vio/core/src/offer/types/SearchOffer'
import {getNightlyPriceForAllRooms} from '@vio/core/src/price/business/price'
import {getRoundingStrategy} from '@vio/core/src/utils/number'
import {PriceSize} from '@vio/shared/src/search/Price'
import {PriceComposition} from '@vio/shared/src/search/PriceComposition'
import {usePointsPriceDisplay} from '@vio/shared/src/whiteLabel/hooks/usePointsPriceDisplay'
import {PointsPrice} from '@vio/shared/src/whiteLabel/PointsPrice'

interface SplitBookingProps {
  className?: string
  showPriceIndication?: boolean
  indicationColor?: string
  indicationSize?: 'bodyXS' | 'bodyS'
  size?: PriceSize
  alwaysShowNightlyPrice?: boolean
  anchorPrice?: number
  anchorPriceTotal?: number
  showAnchorPrice?: boolean
  showAppIcon?: boolean
  hotelId: string
  dataId?: string
  hidePointsCaption?: boolean
}

interface BasePriceProps {
  offer: SearchOffer
  isPrivateDeal: boolean
  anchorPrice?: number
  shouldDisplayInline?: boolean
  shouldAlignOffer?: boolean
  showRowLayout?: boolean
  showPriceIndication?: boolean
  indicationColor?: string
  indicationSize?: 'bodyXS' | 'bodyS'
  size?: PriceSize
  hotelId: string
  isSplitBooking?: boolean
  anchorPriceTotal?: number
  hidePointsCaption?: boolean
  showAppIcon?: boolean
  className?: string
  showAnchorPrice?: boolean
  alwaysShowNightlyPrice?: boolean
  dataId?: string
  isAppLockedDeal?: boolean
}

const SplitBookingPrice = ({
  anchorPrice,
  anchorPriceTotal,
  showAnchorPrice = false,
  className,
  showPriceIndication,
  indicationColor,
  indicationSize,
  size = 'xl',
  alwaysShowNightlyPrice = false,
  hotelId,
  dataId,
  hidePointsCaption = false
}: SplitBookingProps) => {
  const currencyCode = useSelector(getCurrencyCode)
  const points = usePointsPriceDisplay()
  const sbPriceSummary = useSelector(state =>
    getSplitBookingPriceSummary(state, hotelId)
  )
  const combinedAveragePriceNightly = sbPriceSummary.average
  const combinedPriceTotal = sbPriceSummary.sum
  const showTotalPrices =
    useSelector(getShowTotalPrices) && !alwaysShowNightlyPrice

  const price = showTotalPrices
    ? combinedPriceTotal
    : combinedAveragePriceNightly

  if (points.isOn) {
    return (
      <PointsPrice
        {...points.splitParts(price)}
        currencyCode={currencyCode}
        showMaxPointsCaption={!hidePointsCaption}
        size={size}
        dataId={dataId}
      />
    )
  }

  return (
    <PriceComposition
      dataId={dataId}
      className={className}
      showAnchorPrice={
        showAnchorPrice &&
        hasAnchorPrice(combinedAveragePriceNightly, anchorPrice)
      }
      anchorPrice={showTotalPrices ? anchorPriceTotal : anchorPrice}
      currencyCode={currencyCode}
      price={showTotalPrices ? combinedPriceTotal : combinedAveragePriceNightly}
      size={size}
      showIndication={showPriceIndication}
      indicationColor={indicationColor}
      indicationSize={indicationSize}
      roundingStrategy={getRoundingStrategy()}
      showTotal={showTotalPrices}
    />
  )
}

const BasePrice = ({
  offer,
  anchorPrice,
  shouldDisplayInline = false,
  shouldAlignOffer = false,
  showRowLayout = false,
  showPriceIndication = false,
  indicationColor,
  indicationSize,
  size = 'xl',
  hotelId,
  anchorPriceTotal,
  showAppIcon = false,
  className = '',
  showAnchorPrice = true,
  alwaysShowNightlyPrice = false,
  dataId,
  isAppLockedDeal = false,
  isPrivateDeal,
  hidePointsCaption = false
}: BasePriceProps) => {
  const isMultiRoomBundlesSearch = useSelector(getIsMultiRoomBundlesSearch)

  const numberOfRooms = useSelector(state => getNumberOfRooms(state, hotelId))
  const numberOfGuests = useSelector(getNumberOfGuests)

  const currencyCode = useSelector(getCurrencyCode)
  const showTotalPrices =
    useSelector(getShowTotalPrices) && !alwaysShowNightlyPrice
  const points = usePointsPriceDisplay()

  const clickedOfferRate = useSelector(getClickedOfferRate)

  const {nightlyRate, hasAnchorPrice, totalRate} = offer

  const {isPriceDecrease, isPriceIncrease} = useCheckPriceWatchMismatch({
    hotelId,
    clickedOfferRate
  })

  const hideStrikethroughOnPriceMismatch = Boolean(
    isPriceDecrease || isPriceIncrease
  )

  const isAnchorPriceVisible =
    !hideStrikethroughOnPriceMismatch &&
    Boolean(showAnchorPrice && hasAnchorPrice)

  const calculatedNightlyPrice = getNightlyPriceForAllRooms(
    nightlyRate,
    isMultiRoomBundlesSearch,
    numberOfRooms
  )

  const calculatedAnchorPrice = getNightlyPriceForAllRooms(
    anchorPrice,
    isMultiRoomBundlesSearch,
    numberOfRooms
  )

  const anchorRate = showTotalPrices
    ? anchorPriceTotal * numberOfRooms
    : calculatedAnchorPrice

  if (points.isOn) {
    return (
      <Box display="flex" justifyContent="flex-end">
        <PointsPrice
          {...points.splitParts(
            showTotalPrices ? totalRate : calculatedNightlyPrice
          )}
          currencyCode={currencyCode}
          showMaxPointsCaption={!hidePointsCaption}
          size={size}
          dataId={dataId}
        />
      </Box>
    )
  }

  return (
    <Box display="flex" justifyContent="flex-end">
      <PriceComposition
        dataId={dataId}
        className={className}
        size={size}
        showAppIcon={showAppIcon}
        showRowLayout={showRowLayout}
        showAnchorPrice={isAnchorPriceVisible}
        price={showTotalPrices ? totalRate : calculatedNightlyPrice}
        anchorPrice={anchorRate || undefined}
        currencyCode={currencyCode}
        isPrivateDeal={isPrivateDeal}
        isAppLockedDeal={isAppLockedDeal}
        shouldDisplayInline={shouldDisplayInline}
        shouldAlignOffer={shouldAlignOffer}
        roundingStrategy={getRoundingStrategy()}
        numberOfGuests={numberOfGuests}
        showIndication={showPriceIndication}
        indicationColor={indicationColor}
        indicationSize={indicationSize}
        showTotal={showTotalPrices}
      />
    </Box>
  )
}

type Props = (BasePriceProps | SplitBookingProps) & {isSplitBooking?: boolean}

export const OfferPrice: React.FC<Props> = ({
  isSplitBooking = false,
  ...props
}) => {
  if (isSplitBooking) {
    return <SplitBookingPrice {...props} />
  }

  return <BasePrice {...(props as BasePriceProps)} />
}
